import React, { useRef, useEffect } from 'react';
import NextImage from 'next/image';
import styles from './style.module.scss';
import { buildClassName } from 'utils/build-class-name';
import { useTranslation } from 'next-i18next';
import { LOCALE_HOME_PATHS, Namespaces } from 'types/locales';

export enum Rankings {
  RealTrends = '/assets/images/exp-homepage/real_trends_logo.png',
  PowerBroker = '/assets/images/exp-homepage/power_broker_logo.png'
}

export interface AwardItemProps {
  rank?: number;
  text?: string;
  providerImage: string;
  providerImageWidth?: number;
  providerImageHeight?: number;
  textClassName?: string;
  awardClassName?: string;
}

interface AwardCarouselProps {
  awards: AwardItemProps[];
  titleClassName?: string;
}

export default function AwardCarousel({ titleClassName, awards }: AwardCarouselProps) {
  const { t } = useTranslation(Namespaces.HOME);

  const AwardItem = ({ rank, text, providerImage, providerImageWidth, providerImageHeight, textClassName, awardClassName }: AwardItemProps) => (
    <div className={buildClassName(rank && styles['award-item'])}>
      {rank && 
        <div className={styles['award-rank-container']}>
          <p className={styles['award-rank']}>{rank}</p>
        </div>
      }
      <div className={buildClassName(rank && styles['award-name-container'], !rank && styles['row-award'], awardClassName && styles[awardClassName])}>
        <NextImage src={providerImage} alt="Rankings provider" width={providerImageWidth} height={providerImageHeight} />
        <p className={buildClassName(textClassName && styles[textClassName])}>{text}</p>
      </div>
    </div>
  );

  const carouselRef = useRef(null);
  const doubledChildren = [...awards, ...awards];

  useEffect(() => {
    const updateCarouselWidth = () => {
      const carousel = carouselRef.current;
      if (carousel) {
        const slides = [...carousel.getElementsByClassName(styles['carousel-item'])] as HTMLElement[];
        let totalWidth = 0;

        slides.forEach((slide: HTMLElement) => {
          totalWidth += slide.offsetWidth + 40;
        });

        const style = document.createElement('style');
        style.innerHTML = `
          @keyframes slideAwardCarousel {
            0% { transform: translateX(0px); }
            100% { transform: translateX(-${totalWidth / 2}px); }
          }
        `;
        document.head.appendChild(style);

        carouselRef.current.style.animation = 'slideAwardCarousel 50s linear infinite';
      }
    };

    updateCarouselWidth();

    window.addEventListener('resize', updateCarouselWidth);
    return () => window.removeEventListener('resize', updateCarouselWidth);
  }, []);

  return (
    <div className={styles['carousel-container']}>
      <p className={buildClassName(styles['carousel-title'], titleClassName && titleClassName)}>{t(LOCALE_HOME_PATHS.AWARD_CAROUSEL_TITLE, { defaultValue: 'Awards & Recognition' })}</p>
      <div className={styles['carousel-inner']} ref={carouselRef}>
        {doubledChildren.map((child, index) => (
          <div className={styles['carousel-item']} key={index}>
            <AwardItem {...child} />
          </div>
        ))}
      </div>
    </div>
  );
}
